import React, { useEffect, useState } from "react";
import logoCream from "./assets/logo-cream.png";

export default function ConsoomePreOnboardingMotion({ onStart, onSkip, onSignIn, onWhyConsoome, onFinished }) {
  const [ready, setReady] = useState(false);
  const [heroImgLoaded, setHeroImgLoaded] = useState(false);

  useEffect(() => {
    // Fade in after mount
    const t = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(t);
  }, []);

  const startAction = typeof onStart === "function" ? onStart : () => {};
  const signInAction = typeof onSignIn === "function" ? onSignIn : () => {};
  const whyAction = typeof onWhyConsoome === "function" ? onWhyConsoome : () => {};

  return (
    <div style={styles.shell}>
      <style>{`
        @keyframes heroGlow {
          0% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.7; transform: translate(-50%, -50%) scale(1.1); }
          100% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes heroPulse {
          0% { box-shadow: 0 12px 36px ${GOLD}55; }
          50% { box-shadow: 0 16px 48px ${GOLD}77; }
          100% { box-shadow: 0 12px 36px ${GOLD}55; }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* === HERO SECTION === */}
      <div style={styles.heroSection}>
        {/* Background layers */}
        <div style={styles.heroBg} />

        {/* Hero food image with gradient overlay */}
        <img
          src="/hero-food.jpg"
          alt=""
          onLoad={() => setHeroImgLoaded(true)}
          onError={() => setHeroImgLoaded(false)}
          style={{
            ...styles.heroImg,
            opacity: heroImgLoaded ? 0.35 : 0,
          }}
        />

        {/* Atmospheric gold glow */}
        <div style={styles.goldGlow} />
        <div style={styles.goldGlowBottom} />

        {/* Gradient overlay for text readability */}
        <div style={styles.heroOverlay} />

        {/* Hero content */}
        <div style={{
          ...styles.heroContent,
          opacity: ready ? 1 : 0,
          transition: "opacity 0.8s ease",
        }}>
          {/* Logo */}
          <img
            src={logoCream}
            alt="Consoome"
            style={{
              ...styles.logo,
              animation: ready ? "fadeSlideUp 0.7s ease forwards" : "none",
            }}
          />

          {/* Headline */}
          <h1 style={{
            ...styles.headline,
            animation: ready ? "fadeSlideUp 0.7s ease 0.15s forwards" : "none",
            opacity: 0,
          }}>
            Know exactly what to order.
          </h1>

          <p style={{
            ...styles.subhead,
            animation: ready ? "fadeSlideUp 0.7s ease 0.3s forwards" : "none",
            opacity: 0,
          }}>
            Everywhere you dine.
          </p>

          <p style={{
            ...styles.valueLine,
            animation: ready ? "fadeSlideUp 0.7s ease 0.45s forwards" : "none",
            opacity: 0,
          }}>
            Build your Food ID in 60 seconds — allergy-safe, taste-matched dining.
          </p>

          {/* Primary CTA */}
          <button
            onClick={startAction}
            style={{
              ...styles.ctaPrimary,
              animation: ready ? "fadeSlideUp 0.7s ease 0.6s forwards" : "none",
              opacity: 0,
            }}
          >
            Create My Food ID
          </button>

          {/* Sign in link */}
          <p style={{
            ...styles.signInLine,
            animation: ready ? "fadeIn 0.6s ease 0.8s forwards" : "none",
            opacity: 0,
          }}>
            Already have an account?{" "}
            <button onClick={signInAction} style={styles.signInLink}>
              Sign in
            </button>
          </p>
        </div>
      </div>

      {/* === VALUE CARDS (below fold) === */}
      <div style={styles.valueSection}>
        <div style={styles.valueGrid}>
          {VALUE_CARDS.map((card, i) => (
            <div key={card.title} style={{
              ...styles.valueCard,
              animation: ready ? `fadeSlideUp 0.6s ease ${0.9 + i * 0.12}s forwards` : "none",
              opacity: 0,
            }}>
              <div style={styles.valueIcon}>{card.icon}</div>
              <div style={styles.valueTitle}>{card.title}</div>
              <div style={styles.valueDesc}>{card.desc}</div>
            </div>
          ))}
        </div>

        {/* Why Consoome link */}
        <button onClick={whyAction} style={{
          ...styles.whyLink,
          animation: ready ? "fadeIn 0.6s ease 1.3s forwards" : "none",
          opacity: 0,
        }}>
          Learn how it works →
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────
const VALUE_CARDS = [
  {
    icon: "🛡",
    title: "Safe first",
    desc: "Allergies and restrictions filtered before you see the menu.",
  },
  {
    icon: "✦",
    title: "Taste-matched",
    desc: "Dishes ranked to your exact palate across 70+ Dubai venues.",
  },
  {
    icon: "↗",
    title: "Portable",
    desc: "One Food ID. Every restaurant. Share with hotels in one tap.",
  },
];

// ─────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────
const GOLD = "#C8A951";
const GOLD_DARK = "#A68A3E";
const CREAM = "#FFF9EC";
const INK = "#1A1A1A";

const styles = {
  shell: {
    minHeight: "100vh",
    background: INK,
    color: CREAM,
    fontFamily: "'Montserrat', sans-serif",
    overflowX: "hidden",
  },

  // --- Hero ---
  heroSection: {
    position: "relative",
    minHeight: "100vh",
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  heroBg: {
    position: "absolute",
    inset: 0,
    background: `
      radial-gradient(ellipse at 50% 20%, ${GOLD}18, transparent 60%),
      radial-gradient(ellipse at 20% 80%, ${GOLD}0A, transparent 50%),
      radial-gradient(ellipse at 80% 60%, rgba(255,255,255,0.02), transparent 40%),
      linear-gradient(180deg, #1e1d1a 0%, ${INK} 40%, #1a1918 100%)
    `,
    pointerEvents: "none",
  },
  heroImg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "opacity 1s ease",
    pointerEvents: "none",
  },
  goldGlow: {
    position: "absolute",
    left: "50%",
    top: "18%",
    width: 500,
    height: 500,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${GOLD}22, ${GOLD}08 40%, transparent 70%)`,
    filter: "blur(60px)",
    pointerEvents: "none",
    animation: "heroGlow 6s ease-in-out infinite",
  },
  goldGlowBottom: {
    position: "absolute",
    left: "50%",
    bottom: "10%",
    width: 300,
    height: 200,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${GOLD}15, transparent 70%)`,
    filter: "blur(50px)",
    pointerEvents: "none",
    transform: "translateX(-50%)",
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: `linear-gradient(
      180deg,
      rgba(26,26,26,0.3) 0%,
      rgba(26,26,26,0.1) 30%,
      rgba(26,26,26,0.4) 70%,
      ${INK} 100%
    )`,
    pointerEvents: "none",
  },
  heroContent: {
    position: "relative",
    zIndex: 2,
    textAlign: "center",
    padding: "0 24px",
    maxWidth: 560,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  logo: {
    height: 28,
    objectFit: "contain",
    marginBottom: 40,
    opacity: 0.9,
  },
  headline: {
    margin: 0,
    fontSize: "clamp(32px, 7vw, 52px)",
    lineHeight: 1.08,
    fontWeight: 700,
    letterSpacing: "-0.025em",
    color: CREAM,
  },
  subhead: {
    margin: "12px 0 0",
    fontSize: "clamp(20px, 4.5vw, 32px)",
    lineHeight: 1.2,
    fontWeight: 400,
    color: `${CREAM}CC`,
    letterSpacing: "-0.01em",
  },
  valueLine: {
    margin: "20px 0 0",
    fontSize: "clamp(13px, 3vw, 16px)",
    lineHeight: 1.6,
    fontWeight: 300,
    color: `${CREAM}99`,
    maxWidth: 400,
  },
  ctaPrimary: {
    marginTop: 36,
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DARK})`,
    color: INK,
    border: "none",
    borderRadius: 999,
    padding: "17px 44px",
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: "0.02em",
    fontFamily: "'Montserrat', sans-serif",
    cursor: "pointer",
    animation: "heroPulse 3s ease-in-out infinite",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    minHeight: 54,
    width: "100%",
    maxWidth: 340,
  },
  signInLine: {
    marginTop: 20,
    fontSize: 14,
    fontWeight: 400,
    color: `${CREAM}88`,
    fontFamily: "'Montserrat', sans-serif",
  },
  signInLink: {
    background: "none",
    border: "none",
    color: CREAM,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "'Montserrat', sans-serif",
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    padding: "4px 2px",
    minHeight: 44,
    minWidth: 44,
  },

  // --- Value cards ---
  valueSection: {
    position: "relative",
    padding: "48px 20px 60px",
    textAlign: "center",
  },
  valueGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 14,
    maxWidth: 420,
    margin: "0 auto",
  },
  valueCard: {
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${GOLD}1A`,
    borderRadius: 20,
    padding: "20px 20px",
    textAlign: "left",
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
  },
  valueIcon: {
    fontSize: 22,
    width: 36,
    height: 36,
    borderRadius: 10,
    background: `${GOLD}14`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  valueTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: CREAM,
    fontFamily: "'Montserrat', sans-serif",
    marginBottom: 4,
  },
  valueDesc: {
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.55,
    color: `${CREAM}AA`,
    fontFamily: "'Montserrat', sans-serif",
  },
  whyLink: {
    marginTop: 32,
    background: "none",
    border: "none",
    color: GOLD,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "'Montserrat', sans-serif",
    cursor: "pointer",
    letterSpacing: "0.02em",
    padding: "12px 24px",
    minHeight: 44,
  },
};
